import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Checkbox,
  Fieldset,
  Form,
  Input,
  Layout,
  Modal,
  PageWrap,
  Spinner,
  Table,
  Tabs,
} from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import { FormField } from '../../components/FormField/FormField';
import { NumericFormField } from '../../components/FormFields/NumericFormField';
import { PaginationComponent } from '../../components/Pagination';
import {
  CarOptionsConfig,
  CarOptionsPrFamilies,
  CarOptionTabs,
  TabListType,
} from '../../resources/base/models/carOptions';
import { calculateQuote, getQuoteVehicleOptions } from '../../services/api/miles/miles-services';
import { VehicleOption, VehicleOptionCalculateQuote } from '../../services/api/miles/types';
import { scrollToElement } from '../../services/common/form';
import { formatPrice } from '../../services/common/format';
import { store } from '../../services/redux';
import { loadingActions, loadingSelectors } from '../../services/redux/features/loading.slice';
import { useNavigate } from '../../services/routing/navigate';
import { calculateTotalOptionsPrice } from '../FormPage/Step1/initial-values-financial';
import { roundNumber } from '../utils';
import { CarOptionsModalSchema } from './validationSchema';

interface CheckboxProps {
  id: string | number;
  onChecked: (val?) => void;
  value?: boolean;
  options?: any;
  isDisabled?: boolean;
}

type InitialValuesType = {
  optionPriceAmount: number;
};

const TabItemCheckbox: FunctionComponent<CheckboxProps> = ({ id, onChecked, value, options, isDisabled }) => (
  <Checkbox
    name={`tabItemSelected_${id}`}
    disabled={isDisabled}
    onChange={(e) => {
      onChecked(e.target.checked);
      const array = [...options];
      array.find((obj) => obj.prTypeId === id).checked = e.target.checked;
    }}
    checked={value}
  />
);

const validPrFamilies = [
  CarOptionsPrFamilies.Options,
  CarOptionsPrFamilies.Color,
  CarOptionsPrFamilies.Rim,
  CarOptionsPrFamilies.Upholstery,
  CarOptionsPrFamilies.Pack,
];

interface CarOptionsModalProps {
  visible: boolean;
  onCancel: (val?) => void;
  onConfirm: (val?) => void;
  quoteId?: string;
  resetPopupValues: boolean;
  setResetPopupValues: (val) => void;
  prefillOptions?: Array<QuoteSchema.Schemas.VehicleOptionGetQuote>;
  isReadonly?: boolean;
}

export const CarOptionsModal: FunctionComponent<CarOptionsModalProps> = (props) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterText, setFilterText] = useState<string>('');
  const [summaryList, setSummaryList] = useState<TabListType[]>([]);
  const [previousSummaryListValue, setPreviousSummaryListValue] = useState<TabListType[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState<TabListType[]>([]);
  const [optionPriceAmountValue, setOptionPriceAmountValue] = useState<number>();

  const navigate = useNavigate();
  const isLoading = useSelector(loadingSelectors.selectLoading);

  const { visible, onCancel, onConfirm, quoteId, resetPopupValues, setResetPopupValues, prefillOptions, isReadonly } =
    props;
  const formState = useRef({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [confirmationDisabled, setConfirmationDisabled] = useState<boolean>(true);

  const [initialValues, setInitialValues] = useState<InitialValuesType>({
    optionPriceAmount: 0,
  });
  const [savedFormikValue, setSavedFormikValue] = useState<InitialValuesType>(initialValues);

  useEffect(() => {
    if (Array.isArray(prefillOptions) && prefillOptions.length > 0) {
      // 400003 - Total options value
      const optionPriceAmount = prefillOptions.find((o) => o.prKey === '400003')?.optionPriceAmount?.grossAmount ?? 0;
      setOptionPriceAmountValue(optionPriceAmount);
      setInitialValues((prevValue) => {
        const newVal = { ...prevValue, optionPriceAmount: optionPriceAmount ?? 0 };
        return newVal;
      });

      setSavedFormikValue((prevValue) => ({ ...prevValue, optionPriceAmount: optionPriceAmount ?? 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOptions = useCallback(async () => {
    store.dispatch(loadingActions.setLoading(true));
    try {
      if (!quoteId) {
        throw new Error('Missing quote Id!');
      }

      const optionsList = await getQuoteVehicleOptions(quoteId);

      // PC-2642 - filter out 'Total Options' option
      const filteredOptionList = optionsList?.vehicleData?.model?.options?.filter((opt) => opt.prTypeId !== '400003');
      const validPrFamiliesOptions = filteredOptionList?.filter(
        (item) =>
          item.prTypeId && item.prFamily && item.prFamily && (validPrFamilies as Array<string>).includes(item?.prFamily)
      );

      if (validPrFamiliesOptions && validPrFamiliesOptions.length) {
        const mappedOptions: Array<TabListType> = validPrFamiliesOptions.map((item) => ({
          // TODO - there is a mistake in mapping in BFT here, prKey should === prKey, not prTypeId
          checked: !!prefillOptions?.find((p) => p.prKey === item.prTypeId && p.isSelected),
          label: item.name ?? '',
          prFamily: item.prFamily ?? '',
          priceHT: item?.optionPriceAmount?.netAmount ?? item?.optionPriceAmount?.amount ?? 0,
          priceTTC: item?.optionPriceAmount?.grossAmount ?? item?.optionPriceAmount?.amount ?? 0,
          prKey: item.prKey ?? '',
          prTypeId: item.prTypeId ?? '',
          codeOption: item.prKey ?? '',
        }));

        if (Array.isArray(prefillOptions) && prefillOptions.length) {
          const selectedItems = mappedOptions.filter((m) => m.checked);
          setSummaryList(selectedItems);
          setPreviousSummaryListValue(selectedItems);
        }

        setVehicleOptions(mappedOptions);
      }
    } catch {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, quoteId]);

  useEffect(() => {
    if (visible && vehicleOptions.length === 0) {
      getOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (errors !== undefined && Object.entries(errors).length !== 0 && errors.constructor === Object) {
      scrollToElement('.is-error', 'start');
    }
  }, [errors, touched]);

  useEffect(() => {
    document.title = t('carOptions:title');
  }, [t]);

  const postOptions = async (
    allVehicleOptions: VehicleOption[],
    optionPriceAmountValue: number | undefined
  ): Promise<Array<QuoteSchema.Schemas.VehicleOptionGetQuote>> => {
    store.dispatch(loadingActions.setLoading(true));
    const vehicleOptionsMapped: Array<VehicleOptionCalculateQuote> = allVehicleOptions.map((item) => {
      return {
        prTypeId: item.prTypeId,
        isSelected: item.checked ? item.checked : false,
      };
    });

    if (optionPriceAmountValue !== undefined) {
      vehicleOptionsMapped.push({
        isSelected: optionPriceAmountValue > 0,
        prTypeId: '400003',
        optionPriceAmount: {
          amount: Number(roundNumber(optionPriceAmountValue / 1.2)), // calculating net amount from gross assuming 20% VAT
        },
      });
    }

    try {
      if (!quoteId) {
        throw new Error('Missing quote Id!');
      }
      const response = await calculateQuote(quoteId, {
        vehicleData: {
          model: {
            options: [...vehicleOptionsMapped],
          },
        },
      });

      return response.vehicleData.model?.options ?? [];
    } catch (error) {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }

    return [];
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = async (values) => {
    /* mock */
  };

  const resetPage = (): void => {
    setCurrentPage(0);
    setFilterText('');
  };

  const renderPagination = (pages: number) => {
    return (
      <Fieldset>
        <Fieldset.Row>
          <Layout>
            <PaginationComponent
              pagination={{
                currentPage: currentPage + 1,
                pageSize: CarOptionsConfig.itemsPerPage,
                totalCount: pages * CarOptionsConfig.itemsPerPage,
                totalPages: pages,
              }}
              onChange={(page) => {
                setCurrentPage(page - 1);
              }}
            />
          </Layout>
        </Fieldset.Row>
      </Fieldset>
    );
  };

  const filterItems = (items: TabListType[]): TabListType[] => {
    let filtered = items.filter((item) => {
      return item !== undefined;
    });
    if (filterText === '') {
      return filtered;
    }
    filtered = items.filter((obj) =>
      Object.values(obj).some((val) => val.toString().toLowerCase().includes(filterText.toLowerCase()))
    );
    return filtered;
  };

  const getTabListOfPage = (tabId: string, perPage: number): { items: TabListType[]; itemsTotal: number } => {
    let allItems: TabListType[] = [];

    allItems =
      tabId === CarOptionTabs.SUMMARY
        ? summaryList
        : vehicleOptions.filter((item) => item?.prFamily === CarOptionsPrFamilies?.[tabId]);
    allItems = filterItems(allItems);

    const itemsTotal = allItems.length;
    const tabItems: TabListType[] = [];
    const curPage = currentPage <= 0 ? 1 : currentPage + 1;

    if (allItems.length > 0) {
      for (let i = (curPage - 1) * perPage; i < curPage * perPage && i < allItems.length; i += 1) {
        tabItems.push(allItems[i]);
      }
    }

    return { items: tabItems, itemsTotal };
  };

  const updateSummaryList = (newItem: TabListType, isChecked: boolean) => {
    setSummaryList((prevList) => {
      const itemExists = prevList.some((item) => item.prTypeId === newItem.prTypeId);

      if (isChecked) {
        return itemExists ? prevList : [...prevList, newItem];
      }

      return prevList.filter((item) => item.prTypeId !== newItem.prTypeId);
    });
  };

  const tabList = (tabId: string) => {
    const { items, itemsTotal } = getTabListOfPage(tabId, CarOptionsConfig.itemsPerPage);
    const numberOfPages = Math.ceil(itemsTotal / CarOptionsConfig.itemsPerPage);

    return (
      <>
        <Table>
          <Table.Thead>
            <Table.Tr key={tabId}>
              <Table.Th>{t('carOptions:columns:codeOption')}</Table.Th>
              <Table.Th>{t('carOptions:columns:label')}</Table.Th>
              <Table.Th>{t('carOptions:columns:priceExclTax')}</Table.Th>
              <Table.Th>{t('carOptions:columns:priceInclTax')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody testId={`table-head-${tabId}`}>
            {items?.map((item) => (
              <Table.Tr key={item.prTypeId}>
                <Table.Td>
                  {tabId !== CarOptionTabs.SUMMARY && (
                    <TabItemCheckbox
                      id={item.prTypeId}
                      isDisabled={isReadonly}
                      onChecked={(checked) => {
                        updateSummaryList(item, checked);
                      }}
                      value={item.checked}
                      options={vehicleOptions}
                    />
                  )}
                  {item.prKey}
                </Table.Td>
                <Table.Td>{item.label}</Table.Td>
                <Table.Td>{formatPrice(item.priceHT)}</Table.Td>
                <Table.Td>{formatPrice(item.priceTTC)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {renderPagination(numberOfPages)}
      </>
    );
  };

  const renderFreeFormInput = () => {
    return (
      <Fieldset>
        <Layout>
          <Fieldset.Row>
            <Layout.Item default="1/1" s="1/1">
              <NumericFormField
                fieldName="optionPriceAmount"
                type="money"
                maximumFractionDigits={2}
                labelTranslationKey="carOptions:freeOptionsAmount"
                onValueChange={(optionPriceAmount) => setOptionPriceAmountValue(optionPriceAmount)}
                optional
              />
            </Layout.Item>
          </Fieldset.Row>
        </Layout>
      </Fieldset>
    );
  };

  const renderFilterInput = () => {
    return (
      <Fieldset>
        <Layout>
          <Fieldset.Row>
            <Layout.Item default="1/1" s="1/1">
              <FormField
                type="input"
                name="filterResults"
                labelText={t('carOptions:filterResults')}
                render={(fieldProps) => (
                  <Input
                    {...fieldProps}
                    onChange={(e) => {
                      setFilterText(e.target.value);
                      setCurrentPage(0);
                    }}
                    value={filterText}
                  />
                )}
              />
            </Layout.Item>
          </Fieldset.Row>
        </Layout>
      </Fieldset>
    );
  };

  const renderTabsSection = () => {
    return (
      <Tabs defaultSelected="tab0">
        <Tabs.Header aria-label="Tabs Navigation">
          <Tabs.HeaderItem id="tab0" tabId="tab0" onClick={resetPage}>
            {t('carOptions:tabs:Options')}
          </Tabs.HeaderItem>
          <Tabs.HeaderItem id="tab1" tabId="tab1" onClick={resetPage}>
            {t('carOptions:tabs:Color')}
          </Tabs.HeaderItem>
          <Tabs.HeaderItem id="tab2" tabId="tab2" onClick={resetPage}>
            {t('carOptions:tabs:Rim')}
          </Tabs.HeaderItem>
          <Tabs.HeaderItem id="tab3" tabId="tab3" onClick={resetPage}>
            {t('carOptions:tabs:Upholstery')}
          </Tabs.HeaderItem>
          <Tabs.HeaderItem id="tab4" tabId="tab4" onClick={resetPage}>
            {t('carOptions:tabs:Pack')}
          </Tabs.HeaderItem>
          <Tabs.HeaderItem id="tab5" tabId="tab5" onClick={resetPage}>
            {t('carOptions:tabs:summary')}
          </Tabs.HeaderItem>
        </Tabs.Header>
        <Tabs.Content>
          <Tabs.ContentItem aria-labelledby="tab0" tabId="tab0">
            {renderFilterInput()}
            {tabList(CarOptionTabs.Options)}
          </Tabs.ContentItem>
          <Tabs.ContentItem aria-labelledby="tab1" tabId="tab1">
            {renderFilterInput()}
            {tabList(CarOptionTabs.Color)}
          </Tabs.ContentItem>
          <Tabs.ContentItem aria-labelledby="tab2" tabId="tab2">
            {renderFilterInput()}
            {tabList(CarOptionTabs.Rim)}
          </Tabs.ContentItem>
          <Tabs.ContentItem aria-labelledby="tab3" tabId="tab3">
            {renderFilterInput()}
            {tabList(CarOptionTabs.Upholstery)}
          </Tabs.ContentItem>
          <Tabs.ContentItem aria-labelledby="tab4" tabId="tab4">
            {renderFilterInput()}
            {tabList(CarOptionTabs.Pack)}
          </Tabs.ContentItem>
          <Tabs.ContentItem aria-labelledby="tab5" tabId="tab5">
            {renderFreeFormInput()}
            {renderFilterInput()}
            {tabList(CarOptionTabs.SUMMARY)}
          </Tabs.ContentItem>
        </Tabs.Content>
      </Tabs>
    );
  };

  const formRef: any = useRef();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (resetPopupValues) {
      const resetValues = { optionPriceAmount: 0 };
      formRef.current?.resetForm({ values: resetValues });
      setSavedFormikValue(resetValues);
      setSummaryList([]);
      setPreviousSummaryListValue([]);
      setVehicleOptions(vehicleOptions.map((v) => ({ ...v, checked: false })));
      setResetPopupValues(false);
      setFilterText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPopupValues]);

  return (
    <Modal
      id="carOptionsModal"
      title={t('carOptions:title')}
      buttonConfirmText={isReadonly ? null : t('carOptions:apply')} // Only show confirm button if not read only
      buttonCancelText={t('carOptions:cancel')}
      onCancel={() => {
        const updatedVehicleOptions = vehicleOptions.map((vehiclesOptions: TabListType) => ({
          ...vehiclesOptions,
          checked: previousSummaryListValue.some((item) => item.prTypeId === vehiclesOptions.prTypeId),
        }));

        setVehicleOptions(updatedVehicleOptions);
        setSummaryList([...previousSummaryListValue]);
        formRef.current?.resetForm({ values: savedFormikValue });
        onCancel('close');
      }}
      onConfirm={async () => {
        const currentValues: InitialValuesType = formRef.current?.values;
        setSavedFormikValue(currentValues);
        setPreviousSummaryListValue([...summaryList]);
        const options = await postOptions(vehicleOptions, optionPriceAmountValue);

        onConfirm(roundNumber(calculateTotalOptionsPrice(options)));
      }}
      confirmationDisabled={confirmationDisabled}
      shown={visible}
      onClose={() => onCancel('close')}
      asPortal
      className="c-modal--large"
      style={{ overflow: 'hidden' }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={CarOptionsModalSchema()}
        onSubmit={(values) => onSubmit(values)}
        innerRef={formRef}
        enableReinitialize
      >
        {(formProps) => {
          const { values, handleSubmit, errors } = formProps;

          setErrors(formProps.errors);
          setTouched(formProps.touched);
          formState.current = {
            values,
            isValid: formProps.isValid,
            dirty: formProps.dirty,
          };
          const handleSubmitOwn = (e) => {
            handleSubmit(e);
          };
          const isValid = Object.keys(errors).length === 0;
          setConfirmationDisabled(!isValid);
          return (
            <Form onSubmit={handleSubmitOwn}>
              <PageWrap>
                <Fieldset>
                  <Fieldset.Row>
                    <Layout className="u-mt">
                      <Layout.Item default="1/1" s="1/1">
                        {renderTabsSection()}
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                </Fieldset>
              </PageWrap>
            </Form>
          );
        }}
      </Formik>
      {/* TODO Remove when Bronson fixes BRONSD-1042 */}
      {isLoading && <Spinner fullPage />}
    </Modal>
  );
};
